import type { GridSortModel } from '@mui/x-data-grid';
import type { LPUserStatus } from 'src/types/lp-users';

export type LPUserTableRow = Record<string, unknown> & { id: string };
export type LPUserTableFilter = { column: string; value: string };
export type LPUserStatusColumnOptions = {
  onChangeStatus?: (userId: string, status: LPUserStatus) => void;
  updatingUserIds?: readonly string[];
};

export const LP_USER_LABELS: Record<string, string> = {
  exhibitor: 'Expositor',
  shopkeeper: 'Lojista',
  contractor: 'Contratante',
  active: 'Ativo',
  inactive: 'Inativo',
};

const FILTER_CHOICES: Record<string, string[]> = {
  status: ['active', 'inactive'],
  type: ['exhibitor', 'shopkeeper', 'contractor'],
};

export const normalizeLPUserText = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toLocaleLowerCase('pt-BR');

export function filterAndSortLPUsers(
  data: LPUserTableRow[],
  searchFields: readonly string[],
  search: string,
  filters: LPUserTableFilter[],
  sortModel: GridSortModel
) {
  const query = normalizeLPUserText(search);
  const filtered = data.filter((row) => {
    if (
      query &&
      !searchFields.some((field) => {
        const value = row[field];
        return (
          normalizeLPUserText(value).includes(query) ||
          normalizeLPUserText(LP_USER_LABELS[String(value)]).includes(query)
        );
      })
    )
      return false;
    return filters.every(({ column, value }) => {
      const filterValue = normalizeLPUserText(value);
      if (!filterValue) return true;
      const choices = FILTER_CHOICES[column] || [];
      const selected = choices.find(
        (choice) =>
          choice === filterValue || normalizeLPUserText(LP_USER_LABELS[choice]) === filterValue
      );
      // A selected "Ativo" option must not match the substring in "inactive"/"Inativo".
      if (selected) return normalizeLPUserText(row[column]) === selected;
      return (
        normalizeLPUserText(row[column]).includes(filterValue) ||
        (['type', 'status'].includes(column) &&
          normalizeLPUserText(LP_USER_LABELS[String(row[column])]).includes(filterValue))
      );
    });
  });
  const sort = sortModel[0];
  if (!sort?.sort) return filtered;
  return filtered.sort((a, b) => {
    const aValue = a[sort.field];
    const bValue = b[sort.field];
    const result =
      typeof aValue === 'number' && typeof bValue === 'number'
        ? aValue - bValue
        : String(aValue ?? '').localeCompare(String(bValue ?? ''), 'pt-BR', {
            numeric: true,
            sensitivity: 'base',
          });
    return sort.sort === 'asc' ? result : -result;
  });
}

export function paginateLPUsers(data: LPUserTableRow[], page: number, pageSize: number) {
  const safePage = Math.min(Math.max(page, 0), Math.max(Math.ceil(data.length / pageSize) - 1, 0));
  return { page: safePage, rows: data.slice(safePage * pageSize, (safePage + 1) * pageSize) };
}
